// ============================================================
// THE COMPANY — Toolbar (top bar)
// ============================================================

import { useCallback, useState, type ChangeEvent } from "react";
import { useCompanyStore } from "../store/useCompanyStore";
import Modal from "./shared/Modal";

export default function Toolbar() {
  const searchQuery = useCompanyStore((s) => s.searchQuery);
  const setSearchQuery = useCompanyStore((s) => s.setSearchQuery);
  const toggleBoardView = useCompanyStore((s) => s.toggleBoardView);
  const boardViewOpen = useCompanyStore((s) => s.boardViewOpen);
  const addRole = useCompanyStore((s) => s.addRole);
  const roles = useCompanyStore((s) => s.roles);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newParentId, setNewParentId] = useState<string | null>(null);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [setSearchQuery],
  );

  const handleAddRole = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const handleCreateRole = useCallback(async () => {
    if (!newName.trim() || !newTitle.trim()) return;
    try {
      await addRole({
        name: newName.trim(),
        title: newTitle.trim(),
        parentId: newParentId,
      });
      setNewName("");
      setNewTitle("");
      setNewParentId(null);
      setShowAddModal(false);
    } catch (err) {
      console.error("Failed to create role:", err);
    }
  }, [addRole, newName, newTitle, newParentId]);

  return (
    <div
      className="flex h-12 shrink-0 items-center gap-4 border-b px-4"
      style={{
        backgroundColor: "var(--bg-surface)",
        borderColor: "var(--border-default)",
      }}
    >
      {/* Left: Logo */}
      <span
        className="shrink-0 text-xs font-bold tracking-[0.2em]"
        style={{ color: "var(--text-primary)" }}
      >
        THE COMPANY
      </span>

      {/* Center: Search */}
      <div className="mx-auto flex w-full max-w-md items-center">
        <div
          className="toolbar-search flex w-full items-center gap-2 rounded-lg border px-3 py-1.5 transition-colors duration-150"
          style={{
            backgroundColor: "var(--bg-elevated)",
            borderColor: "var(--border-subtle)",
          }}
        >
          {/* Search icon */}
          <svg
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: "var(--text-muted)" }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search roles..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full bg-transparent text-xs outline-none placeholder:opacity-50"
            style={{
              color: "var(--text-primary)",
            }}
          />
        </div>
      </div>

      {/* Right: Actions */}
      <div className="flex shrink-0 items-center gap-2">
        {/* Add Role button */}
        <button
          onClick={handleAddRole}
          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white transition-all duration-150"
          style={{ backgroundColor: "var(--accent)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--accent-hover)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "var(--accent)";
          }}
        >
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
          Add Role
        </button>

        {/* Board toggle button */}
        <button
          onClick={toggleBoardView}
          className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-all duration-150"
          style={{
            borderColor: boardViewOpen
              ? "var(--accent)"
              : "var(--border-default)",
            color: boardViewOpen ? "var(--accent)" : "var(--text-secondary)",
            backgroundColor: boardViewOpen
              ? "rgba(99, 102, 241, 0.1)"
              : "transparent",
          }}
          onMouseEnter={(e) => {
            if (!boardViewOpen) {
              e.currentTarget.style.borderColor = "var(--border-strong)";
              e.currentTarget.style.color = "var(--text-primary)";
            }
          }}
          onMouseLeave={(e) => {
            if (!boardViewOpen) {
              e.currentTarget.style.borderColor = "var(--border-default)";
              e.currentTarget.style.color = "var(--text-secondary)";
            }
          }}
        >
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
            />
          </svg>
          Board
        </button>
      </div>

      {/* Add Role Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Role"
        width="400px"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs" style={{ color: "var(--text-muted)" }}>
              Name
            </label>
            <input
              type="text"
              placeholder="e.g. Jordan Lee"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="rounded-md border px-3 py-2 text-sm outline-none"
              style={{
                backgroundColor: "var(--bg-elevated)",
                borderColor: "var(--border-default)",
                color: "var(--text-primary)",
              }}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs" style={{ color: "var(--text-muted)" }}>
              Title
            </label>
            <input
              type="text"
              placeholder="e.g. Social Media Manager"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="rounded-md border px-3 py-2 text-sm outline-none"
              style={{
                backgroundColor: "var(--bg-elevated)",
                borderColor: "var(--border-default)",
                color: "var(--text-primary)",
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs" style={{ color: "var(--text-muted)" }}>
              Reports To
            </label>
            <select
              value={newParentId ?? ""}
              onChange={(e) => setNewParentId(e.target.value || null)}
              className="rounded-md border px-3 py-2 text-sm outline-none"
              style={{
                backgroundColor: "var(--bg-elevated)",
                borderColor: "var(--border-default)",
                color: "var(--text-primary)",
              }}
            >
              <option value="">None (top level)</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} — {r.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowAddModal(false)}
              className="rounded-md border px-4 py-2 text-xs font-medium transition-colors duration-150"
              style={{
                borderColor: "var(--border-default)",
                color: "var(--text-secondary)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--border-strong)";
                e.currentTarget.style.color = "var(--text-primary)";
                e.currentTarget.style.backgroundColor = "var(--bg-elevated)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--border-default)";
                e.currentTarget.style.color = "var(--text-secondary)";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateRole}
              disabled={!newName.trim() || !newTitle.trim()}
              className="rounded-md px-4 py-2 text-xs font-medium text-white transition-colors duration-150 disabled:opacity-40"
              style={{ backgroundColor: "var(--accent)" }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = "var(--accent-hover)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "var(--accent)";
              }}
            >
              Create Role
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
